/**
 * Modal "Inviter un joueur" (mercato équipe).
 *
 * Recherche du joueur par autocomplete (PlayerPicker), message optionnel,
 * confirmation d'envoi. Remplace l'ancienne barre inline (champ pseudo brut
 * + bouton "Inviter").
 */

import { useState } from 'react';
import PlayerPicker, { type PlayerPickerItem } from './PlayerPicker';

interface InviteMercatoModalProps {
  excludePseudos: string[];
  onClose: () => void;
  onSubmit: (pseudo: string, message: string) => Promise<void>;
}

export default function InviteMercatoModal({ excludePseudos, onClose, onSubmit }: InviteMercatoModalProps) {
  const [player, setPlayer] = useState<PlayerPickerItem | null>(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!player) return;
    setError(null);
    setLoading(true);
    try {
      await onSubmit(player.pseudo, message.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cia-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="cia-modal"
        role="dialog"
        aria-labelledby="invite-mercato-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="invite-mercato-title" className="cia-modal-title">Inviter un joueur</h3>
        <p className="cia-modal-subtitle">
          Envoyez une invitation à rejoindre votre équipe. Le joueur devra l'accepter.
        </p>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label className="cia-rdv-field">
            <span>Joueur</span>
            <PlayerPicker
              value={player}
              onChange={setPlayer}
              excludePseudos={excludePseudos}
              placeholder="Nom du joueur…"
              autoFocus
            />
          </label>
          <label className="cia-rdv-field">
            <span>Message (optionnel)</span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Un petit mot pour présenter votre équipe…"
              rows={3}
              style={{
                borderRadius: 8, border: '1px solid var(--border-color, rgba(255,255,255,0.12))',
                background: 'var(--input-bg, rgba(0,0,0,0.2))', color: 'var(--text-primary)',
                padding: '8px 10px', font: 'inherit', resize: 'vertical',
              }}
            />
          </label>
          {error && <p className="cia-modal-error">{error}</p>}
          <div className="cia-modal-actions">
            <button type="button" className="cia-btn cia-btn--ghost" onClick={onClose} disabled={loading}>
              Annuler
            </button>
            <button type="submit" className="cia-btn cia-btn--primary" disabled={loading || !player}>
              {loading ? 'Envoi…' : 'Envoyer l\'invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
